import { ZERO_IN_BASE } from '@shared/utils/constant'
import { BigNumberInWei, BigNumberInBase } from '@injectivelabs/utils'
import { TradeDirection, getDefaultSubaccountId } from '@injectivelabs/sdk-ts'
import type { AccountBalance } from '@/types'
import type {
  PositionV2,
  TokenStatic,
  AccountPortfolioV2
} from '@injectivelabs/sdk-ts'

export const accountBalanceDefaults = {
  usdPrice: 0,
  bankBalance: '0',
  accountTotalBalanceInUsd: '0'
}

export const getSubaccountBalancesMap = (response: AccountPortfolioV2) => {
  const sharedTokenStore = useSharedTokenStore()
  const defaultSubaccountId = getDefaultSubaccountId(response.accountAddress)

  const subaccountListsWithToken = response.subaccountsList
    .map((subaccount) => {
      return {
        ...subaccount,
        token: sharedTokenStore.tokenByDenomOrSymbol(subaccount.denom)
      }
    })
    .filter((subaccount) => subaccount.token)

  const bankBalanceListWithToken = response.bankBalancesList
    .map((bankBalance) => {
      return {
        ...bankBalance,
        token: sharedTokenStore.tokenByDenomOrSymbol(bankBalance.denom)
      }
    })
    .filter(
      (bankBalance) =>
        bankBalance.token && !bankBalance.denom.startsWith('share')
    )

  if (
    bankBalanceListWithToken.length === 0 &&
    subaccountListsWithToken.length === 0
  ) {
    return {
      tokensList: [],
      accountBalancesMap: {}
    }
  }

  const defaultSubaccount = bankBalanceListWithToken.map((bankBalance) => {
    const subaccountBalance = response.subaccountsList.find(
      (balance) =>
        balance.denom === bankBalance.denom &&
        balance.subaccountId === defaultSubaccountId
    )
    const subaccountTotalBalance =
      subaccountBalance?.deposit?.totalBalance || '0'
    const inOrderBalance = subaccountTotalBalance
    const availableMargin = bankBalance.amount
    const unrealizedPnl = ZERO_IN_BASE

    const accountTotalBalance = new BigNumberInWei(bankBalance.amount)
      .plus(subaccountTotalBalance)
      .plus(unrealizedPnl)

    return {
      ...accountBalanceDefaults,
      inOrderBalance,
      availableMargin,
      denom: bankBalance.denom,
      token: bankBalance.token!,
      unrealizedPnl: unrealizedPnl.toFixed(),
      bankBalance: bankBalance?.amount || '0',
      ...(subaccountBalance?.deposit || {
        totalBalance: '0',
        availableBalance: '0'
      }),
      accountTotalBalance: accountTotalBalance.toFixed(0)
    } as AccountBalance
  })

  const nonDefaultSubaccounts = subaccountListsWithToken.reduce(
    (accountBalancesMap, subaccountBalance) => {
      if (subaccountBalance.subaccountId === defaultSubaccountId) {
        return accountBalancesMap
      }

      const existingAccountBalances =
        accountBalancesMap[subaccountBalance.subaccountId] || []
      const subaccountAvailableBalance =
        subaccountBalance?.deposit?.availableBalance || '0'
      const unrealizedPnl = ZERO_IN_BASE

      const subaccountTotalBalance = new BigNumberInWei(
        subaccountBalance?.deposit?.totalBalance || '0'
      ).plus(unrealizedPnl)

      const inOrderBalance = subaccountTotalBalance
        .minus(subaccountAvailableBalance)
        .toFixed()

      return {
        ...accountBalancesMap,
        [subaccountBalance.subaccountId]: [
          ...existingAccountBalances,
          {
            ...accountBalanceDefaults,
            inOrderBalance,
            denom: subaccountBalance.denom,
            token: subaccountBalance.token!,
            unrealizedPnl: unrealizedPnl.toFixed(),
            ...(subaccountBalance.deposit || {
              totalBalance: '0',
              availableBalance: '0'
            }),
            availableMargin: subaccountAvailableBalance,
            accountTotalBalance: subaccountTotalBalance.toFixed(0)
          }
        ]
      }
    },
    {} as Record<string, AccountBalance[]>
  )

  const allTokens = [...subaccountListsWithToken, ...bankBalanceListWithToken]

  return {
    accountBalancesMap: {
      [defaultSubaccountId]: defaultSubaccount,
      ...nonDefaultSubaccounts
    },
    balanceDenoms: [
      ...new Set(
        allTokens.map((subaccountWithToken) => subaccountWithToken.denom)
      )
    ],
    tokensList: [
      ...new Set(
        allTokens
          .map((subaccountWithToken) => subaccountWithToken.token)
          .filter((token) => token) as TokenStatic[]
      )
    ]
  }
}

export const appendPositionsToAccountBalancesMap = (
  accountBalancesMap: Record<
    string /** string = subaccountId */,
    AccountBalance[]
  >,
  positions: PositionV2[]
) => {
  const positionsBySubaccountId = positions.reduce(
    (positions, position) => {
      const existingPositions = positions[position.subaccountId] || []

      positions[position.subaccountId] = [...existingPositions, position]

      return positions
    },
    {} as Record<string, PositionV2[]>
  )

  return Object.keys(accountBalancesMap).reduce(
    (result, subaccountId) => {
      const subaccountPositions = positionsBySubaccountId[subaccountId] || []
      const accountBalances =
        accountBalancesMap[subaccountId as keyof typeof accountBalancesMap]

      if (subaccountPositions.length === 0) {
        return result
      }

      const accountBalancesWithUnrealizedPnl = accountBalances.map(
        (accountBalance) => {
          const assetPositions = subaccountPositions.filter(
            (position) => position.denom === accountBalance.denom
          )

          const unrealizedPnlAndMargin = assetPositions.reduce(
            (total, position) =>
              total
                .plus(position.margin)
                .plus(
                  new BigNumberInWei(position.quantity)
                    .times(
                      new BigNumberInBase(position.markPrice).minus(
                        position.entryPrice
                      )
                    )
                    .times(position.direction === TradeDirection.Long ? 1 : -1)
                ),
            ZERO_IN_BASE
          )

          return {
            ...accountBalance,
            unrealizedPnl: unrealizedPnlAndMargin.toFixed(),
            accountTotalBalance: new BigNumberInBase(
              accountBalance.accountTotalBalance
            )
              .plus(unrealizedPnlAndMargin)
              .toFixed()
          } as AccountBalance
        }
      )

      result[subaccountId] = accountBalancesWithUnrealizedPnl

      return result
    },
    {} as Record<string, AccountBalance[]>
  )
}
